import localforage from 'localforage'

const KEY = 'e2eeEvictionBreadcrumb'
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 400
const CACHE_API_NAME = 'e2ee-eviction-breadcrumb'
const CACHE_API_URL = '/__e2ee_eviction_breadcrumb__'

const lfStore = localforage.createInstance({
  name: 'truple',
  storeName: 'e2eeEvictionTracker'
})

function emptyRecord() {
  return { firstSetupAt: 0, lastSetupAt: 0, setupCount: 0 }
}

function mergeAccountRecords(records) {
  const merged = emptyRecord()
  for(const r of records) {
    if(!r) continue
    if(r.firstSetupAt && (!merged.firstSetupAt || r.firstSetupAt < merged.firstSetupAt)) {
      merged.firstSetupAt = r.firstSetupAt
    }
    if(r.lastSetupAt > merged.lastSetupAt) {
      merged.lastSetupAt = r.lastSetupAt
    }
    if(r.setupCount > merged.setupCount) {
      merged.setupCount = r.setupCount
    }
  }
  return merged
}

// The stored value is a map of accountId → {firstSetupAt, lastSetupAt, setupCount}.
// Reject anything that doesn't match — including the previous flat-record format.
function coerceMap(raw) {
  if(!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  if('setupCount' in raw || 'firstSetupAt' in raw) return null
  return raw
}

function mergeMaps(maps) {
  const grouped = {}
  for(const m of maps) {
    const map = coerceMap(m)
    if(!map) continue
    for(const accountId of Object.keys(map)) {
      if(!grouped[accountId]) grouped[accountId] = []
      grouped[accountId].push(map[accountId])
    }
  }
  const out = {}
  for(const accountId of Object.keys(grouped)) {
    out[accountId] = mergeAccountRecords(grouped[accountId])
  }
  return out
}

function readLocalStorage() {
  try {
    const raw = localStorage.getItem(KEY)
    return raw ? JSON.parse(raw) : null
  } catch(e) {
    return null
  }
}

function writeLocalStorage(map) {
  try {
    localStorage.setItem(KEY, JSON.stringify(map))
  } catch(e) {
    // ignore
  }
}

function readCookie() {
  try {
    const match = document.cookie.split('; ').find(row => row.startsWith(KEY + '='))
    if(!match) return null
    const value = decodeURIComponent(match.substring(KEY.length + 1))
    return JSON.parse(value)
  } catch(e) {
    return null
  }
}

function writeCookie(map) {
  try {
    const value = encodeURIComponent(JSON.stringify(map))
    document.cookie = `${KEY}=${value}; max-age=${COOKIE_MAX_AGE_SECONDS}; path=/; SameSite=Lax`
  } catch(e) {
    // ignore
  }
}

async function readIndexedDB() {
  try {
    return await lfStore.getItem(KEY)
  } catch(e) {
    return null
  }
}

async function writeIndexedDB(map) {
  try {
    await lfStore.setItem(KEY, map)
  } catch(e) {
    // ignore
  }
}

async function readCacheApi() {
  try {
    if(typeof caches === 'undefined') return null
    const cache = await caches.open(CACHE_API_NAME)
    const response = await cache.match(CACHE_API_URL)
    if(!response) return null
    return await response.json()
  } catch(e) {
    return null
  }
}

async function writeCacheApi(map) {
  try {
    if(typeof caches === 'undefined') return
    const cache = await caches.open(CACHE_API_NAME)
    const response = new Response(JSON.stringify(map), {
      headers: { 'Content-Type': 'application/json' }
    })
    await cache.put(CACHE_API_URL, response)
  } catch(e) {
    // ignore
  }
}

class E2EEEvictionTracker {
  async getMergedMap() {
    const [ls, ck, idb, cacheApi] = await Promise.all([
      readLocalStorage(),
      readCookie(),
      readIndexedDB(),
      readCacheApi()
    ])
    return mergeMaps([ls, ck, idb, cacheApi])
  }

  async getRecord(accountId) {
    if(!accountId) return emptyRecord()
    const map = await this.getMergedMap()
    return map[accountId] || emptyRecord()
  }

  async recordSetup(accountId) {
    if(!accountId) return null
    const map = await this.getMergedMap()
    const existing = map[accountId] || emptyRecord()
    const now = Date.now()
    map[accountId] = {
      firstSetupAt: existing.firstSetupAt || now,
      lastSetupAt: now,
      setupCount: (existing.setupCount || 0) + 1
    }
    writeLocalStorage(map)
    writeCookie(map)
    await Promise.all([
      writeIndexedDB(map),
      writeCacheApi(map)
    ])
    return map[accountId]
  }

  /**
   * Returns true when, for this specific account, the user has set up E2EE
   * on this browser at least twice and the average gap between setups is
   * under ~30 days — i.e. their browser is evicting site data.
   */
  async isFrequentlyEvicted(accountId) {
    const record = await this.getRecord(accountId)
    if(record.setupCount < 2) return false
    const span = record.lastSetupAt - record.firstSetupAt
    if(span <= 0) return false
    const avgGapMs = span / (record.setupCount - 1)
    return avgGapMs < 30 * 24 * 60 * 60 * 1000
  }

  async getStorageEstimate() {
    try {
      if(navigator.storage && typeof navigator.storage.estimate === 'function') {
        const { usage, quota } = await navigator.storage.estimate()
        if(quota && usage) {
          return { usage, quota, usageRatio: usage / quota }
        }
      }
    } catch(e) {
      // ignore
    }
    return null
  }
}

export default (new E2EEEvictionTracker())
